import React, { useState, useMemo, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Card, Grid, Dialog, DialogTitle, DialogContent, Tooltip, Fab, Typography } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import { useTheme } from '@mui/material/styles';
import { FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField, Checkbox } from '@mui/material';

import { makeSelectorToGeoJsonNamesDict } from '../../DataPrep';

// Helper function to group by block_name
const groupBy = (array, key) => array.reduce((result, currentValue) => {
    if (currentValue.properties[key] !== null) {
        (result[currentValue.properties[key]] = result[currentValue.properties[key]] || []).push(currentValue);
    }
    return result;
}, {});

// Helper function to calculate percentile
const percentile = (arr, q) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const pos = (sorted.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;
    if (sorted[base + 1] !== undefined) {
        return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
    } else {
        return sorted[base];
    }
};

// Compute average and percentiles for each group
const computeGroupAverages = (groups, columnName) => {
    let groupStats = {};
    for (let group in groups) {
        const groupData = groups[group].map(feature => feature.properties[columnName]).filter(value => !isNaN(value) && value !== undefined && value !== 0);
        if (groupData.length > 0) {
            const mean = groupData.reduce((a, b) => a + b) / groupData.length;
            const pctl10 = percentile(groupData, 0.1);
            const pctl90 = percentile(groupData, 0.9);
            const pctlRange = pctl90/pctl10
            groupStats[group] = { mean, pctlRange };
        }
    }
    return groupStats;
};

// Dashboard for all columns
const Dashboard = ({ features, userGroup }) => {
    const [open, setOpen] = useState(false);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [reversedDict, setReversedDict] = useState({});
    const [availableColumns, setAvailableColumns] = useState([]);
    const [dict, setDict] = useState({});

    useEffect(() => {
        const originalDict = makeSelectorToGeoJsonNamesDict(userGroup);
        const revDict = Object.keys(originalDict).reduce((acc, key) => {
            const value = originalDict[key];
            return { ...acc, [value]: key };
        }, {});
    
        setDict(originalDict);
        setReversedDict(revDict);
        setAvailableColumns(Object.values(revDict));
    
    }, [userGroup]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleDashboardOpen = () => {
        setOpen(true);
    };

    const handleDashboardClose = () => {
        setOpen(false);
    };

    const summaryStatsForColumns = useMemo(() => selectedColumns.map(columnName => {
        const groupedFeatures = groupBy(features, 'block_name');
        const groupStats = computeGroupAverages(groupedFeatures, columnName);
        return { columnName, groupStats };
    }), [features, selectedColumns]);

    const theme = useTheme();

    const getComparator = (order, orderBy) => {
        return order === 'desc'
          ? (a, b) => descendingComparator(a, b, orderBy)
          : (a, b) => -descendingComparator(a, b, orderBy);
      };
      
    const descendingComparator = (a, b, orderBy) => {
        const valA = a.groupStats[orderBy]?.mean;
        const valB = b.groupStats[orderBy]?.mean;
      
        if (valB < valA) {
          return -1;
        }
        if (valB > valA) {
          return 1;
        }
        return 0;
      };

    const sortedStats = useMemo(() => {
        const comparator = getComparator(order, orderBy);
        const stabilizedThisWeek = summaryStatsForColumns.map((el, index) => [el, index]);
        
        stabilizedThisWeek.sort((a, b) => {
          const order = comparator(a[0], b[0]);
          if (order !== 0) return order;
          return a[1] - b[1];
        });
      
        return stabilizedThisWeek.map((el) => el[0]);
    }, [summaryStatsForColumns, order, orderBy]);
    
    const sortedBlockNames = useMemo(() => {
        if (summaryStatsForColumns.length === 0) return [];
    
        const blockNames = Object.keys(summaryStatsForColumns[0]?.groupStats || {});
        
        if (orderBy !== 'block_name') {
            const columnStats = summaryStatsForColumns.find(stats => stats.columnName === orderBy);
            blockNames.sort((a, b) => {
                const valA = columnStats?.groupStats[a]?.mean || 0;
                const valB = columnStats?.groupStats[b]?.mean || 0;
                return order === 'asc' ? valA - valB : valB - valA;
            });
        } else {
            blockNames.sort((a, b) => order === 'asc' ? a.localeCompare(b) : b.localeCompare(a));
        }
        
        return blockNames;
    }, [summaryStatsForColumns, orderBy, order]);

    return (
        <div>
        <Tooltip title={<Typography sx={{ fontSize: 14 }}>Block Summary Viewer</Typography>}>
            <Fab
              color="primary"
              aria-label="add"
              style={{ position: 'fixed', top: 280, right: 10 }}
              onClick={handleDashboardOpen}
            >
              <GridViewIcon />
            </Fab>
        </Tooltip>
          <Dialog open={open} onClose={handleDashboardClose} fullWidth maxWidth="lg">
            <DialogTitle>Metric Summary by Block</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                        <Autocomplete
                            multiple
                            id="columns-autocomplete"
                            options={availableColumns} // use human-readable names as options
                            getOptionLabel={(option) => option} // display human-readable names in the dropdown menu
                            //defaultValue={selectedColumns.map(columnName => reversedDict[columnName]).filter(Boolean)} // temporarily removed for troubleshooting
                            onChange={(event, value) => {
                                setSelectedColumns(value.map(name => dict[name]).filter(Boolean));  // use dict instead of reversedDict to map human-readable names back to original column names.
                            }}
                            renderInput={(params) => (
                                <TextField {...params} variant="standard" label="Columns" placeholder="Choose..." />
                            )}
                        />
                        </FormControl>
                    </Grid>
                    
                </Grid>
                <Typography 
                            sx={{ marginTop:'15px', marginLeft: '15px', color: 'rgba(0, 0, 0, 0.6)' }}
                            id="range-slider-label" 
                            variant="body2"
                        >
                            Average values across plants shown with variability in parentheses.
                        </Typography>
                </DialogContent>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card>
                    <TableContainer>
                      <Table size="small">
                      <TableHead>
                        <TableRow>
                            <TableCell>
                            <TableSortLabel
                                active={orderBy === 'block_name'}
                                direction={orderBy === 'block_name' ? order : 'asc'}
                                onClick={(event) => handleRequestSort(event, 'block_name')}
                            >
                                <b>Block Name</b>
                            </TableSortLabel>
                            </TableCell>
                            {sortedStats.map((item, index) => (
                                <TableCell key={index}>
                                    <TableSortLabel
                                        active={orderBy === item.columnName}
                                        direction={orderBy === item.columnName ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, item.columnName)}
                                    >
                                    <b>{reversedDict[item.columnName]}</b>
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                        </TableHead>

                        <TableBody>
                        {sortedBlockNames.map((blockName, index) => (
                            <TableRow key={index}>
                            <TableCell>
                                <div style={{ 
                                whiteSpace: 'nowrap', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                maxWidth: 160, 
                                paddingRight: theme.spacing(1) 
                                }}>
                                {blockName}
                                </div>
                            </TableCell>
                            {sortedStats.map(item => (
                                <TableCell key={item?.columnName}>
                                {item?.groupStats[blockName] && `${item.groupStats[blockName].mean.toFixed(1)} (${item.groupStats[blockName].pctlRange.toFixed(1)}x)`}
                                </TableCell>
                            ))}
                            </TableRow>
                        ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>
        </div>
    );
};
    
export default Dashboard;